import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import Text from 'components/core/Text';
import { CloseIcon } from 'components/Icons';

import {
  StyledBody,
  StyledClose,
  StyledFooter,
  StyledHeader,
  StyledOverlay,
  StyledPanel,
} from './Modal.styles';
import { TModalProps } from './Modal.types';

const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const Modal = ({
  isOpen,
  onClose,
  title,
  footer,
  children,
  width = 560,
  fullScreen = false,
}: TModalProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Call sites pass an inline arrow, so depending on `onClose` would re-run this effect
  // every render — yanking focus back out of whatever the user is typing in.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    // Remember where focus came from so it can be handed back on close.
    const opener = document.activeElement as HTMLElement | null;

    const focusable = () =>
      Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
        (node) => node.offsetParent !== null
      );

    // Move focus into the dialog; the panel itself is the fallback when it has no controls.
    const first = focusable()[0];
    (first ?? panelRef.current)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      // Trap Tab inside the dialog so focus cannot wander behind the overlay.
      const nodes = focusable();
      if (!nodes.length) {
        event.preventDefault();
        return;
      }

      const edge = event.shiftKey ? nodes[0] : nodes[nodes.length - 1];
      if (document.activeElement === edge || !panelRef.current?.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? nodes[nodes.length - 1] : nodes[0]).focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      opener?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <StyledOverlay
      $fullScreen={fullScreen}
      // Click, not mousedown: a drag that happens to end over the overlay is not a dismissal.
      onClick={onClose}>
      <StyledPanel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        $width={width}
        $fullScreen={fullScreen}
        onClick={(event) => event.stopPropagation()}>
        <StyledHeader>
          <Text id={titleId} variant="sectionTitle" as="h2">
            {title}
          </Text>
          <StyledClose type="button" aria-label="Close" onClick={onClose}>
            <CloseIcon size={18} />
          </StyledClose>
        </StyledHeader>
        <StyledBody $fullScreen={fullScreen}>{children}</StyledBody>
        {footer ? <StyledFooter>{footer}</StyledFooter> : null}
      </StyledPanel>
    </StyledOverlay>,
    document.body
  );
};

export default Modal;
